import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'
import { Button } from 'components/styled/button'
import { useNavigate } from 'react-router-dom'
import find from 'lodash/fp/find'
import { mapLanguageToComponentProps } from 'interview/i18n'
import CandidateService from 'interview/services/candidate'
import { addError } from 'actions'
import { completeInterview } from 'interview/actions/candidate'
import InterviewFormWrapper from 'interview/components/interview-form-wrapper'
import * as analytics from 'analytics/performance'
import ActionBarWrapper from 'components/action-bar-wrapper'
import { Body } from 'components/styled/typography'
import { Confirmation } from 'components/styled/modal/confirmation'
import { earlierDate, formatDateTimeTimezone } from 'utils/date'
import {
  getCandidateInfoForAmplitude,
  getCompanyAndInterviewInfoForAmplitude,
} from 'selectors'
import { MODAL_CLOSE_DELAY_MS } from 'components/styled/modal/modal-component'

const propTypes = {
  candidate: PropTypes.shape({
    uuid: PropTypes.string,
    _id: PropTypes.string,
    videoResponses: PropTypes.array,
    completeUrl: PropTypes.string,
    agreeTOS: PropTypes.bool,
    answerDeadline: PropTypes.string,
  }).isRequired,
  interview: PropTypes.shape({
    endTime: PropTypes.string,
    questions: PropTypes.array,
    formSetup: PropTypes.object,
    language: PropTypes.string,
    additionalFormFields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        required: PropTypes.bool,
      })
    ).isRequired,
    company: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      privacyLink: PropTypes.string,
    }).isRequired,
  }).isRequired,
  videoUploads: PropTypes.arrayOf(PropTypes.string).isRequired,
  i18n: PropTypes.shape({
    interview: PropTypes.shape({
      confirmSubmitHeader: PropTypes.string,
      confirmSubmitContent: PropTypes.string,
      confirmSubmitContentWarning: PropTypes.string,
      submitInterviewHeader: PropTypes.string,
      submitInterviewContent: PropTypes.string,
      confirmSubmitButton: PropTypes.string,
      continueLater: PropTypes.string,
      submit: PropTypes.string,
    }),
    layout: PropTypes.shape({
      endTime: PropTypes.func,
    }),
  }).isRequired,
  recording: PropTypes.bool.isRequired,
  model: PropTypes.string,
  onSubmit: PropTypes.func,
  onContinueLater: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
}

const defaultProps = {
  model: 'candidate',
  onSubmit: undefined,
  onContinueLater: undefined,
}

const mapStateToProps = (state, ownProps) => ({
  candidate:
    ownProps.model === 'previewCandidate'
      ? state.previewCandidate
      : state.candidate,
  interview:
    ownProps.model === 'previewCandidate'
      ? state.interview
      : state.interview.jobOpening,
  videoUploads: state.global.videoUploads,
  recording: state.global.recording,
})

const Interview = (props) => {
  const [missingVideosConfirmed, setMissingVideosConfirmed] = useState(false)
  const [shouldSubmitAdditionalFields, setShouldSubmitAdditionalFields] =
    useState(false)
  const [showRequiredWarning, setShowRequiredWarning] = useState(false)
  const [askConfirmation, setAskConfirmation] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const {
    candidate,
    interview,
    dispatch,
    model,
    i18n,
    videoUploads,
    recording,
    onSubmit,
    onContinueLater,
  } = props

  const onConfirmSubmit = () => {
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)

    if (model === 'previewCandidate' && onSubmit) {
      setTimeout(() => {
        onSubmit()
      }, MODAL_CLOSE_DELAY_MS)
    } else {
      setIsLoading(true)
      analytics.onClickEvent({
        locationId: 'interviewPage',
        id: 'submitInterviewButton',
        properties: {
          ...candidateInfo,
          ...companyAndInterviewInfo,
        },
      })
      dispatch(completeInterview(candidate))
        .then(() => {
          setIsLoading(false)
          if (candidate.completeUrl) {
            window.location.href = candidate.completeUrl
          } else {
            navigate('/enter/finished')
          }
        })
        .catch((err) => {
          setIsLoading(false)
          setAskConfirmation(false)
          const responseBody = err.response && err.response.response
          const requiredQuestionsMissing =
            typeof responseBody === 'string' &&
            responseBody.includes('Required questions have not been answered')
          if (requiredQuestionsMissing) {
            setShowRequiredWarning(true)
            const questionsEl = document.querySelector('.interview-questions')
            if (questionsEl) {
              questionsEl.scrollIntoView({ behavior: 'smooth' })
            }
            return
          }
          dispatch(addError(err))
        })
    }
  }

  const onConfirm = () => {
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)

    setMissingVideosConfirmed(true)
    setAskConfirmation(false)
    onConfirmSubmit()
    if (model === 'candidate') {
      analytics.onClickEvent({
        locationId: 'interviewPage',
        id: 'confirmSendButton',
        properties: {
          ...candidateInfo,
          ...companyAndInterviewInfo,
        },
      })
    }
  }

  const areAllAnswered = () => {
    const { videoResponses } = candidate
    const { questions } = interview
    if (!questions) {
      return true
    }
    let allAnswered = true
    questions.forEach(({ _id: questionId, answerable }) => {
      if (!answerable) {
        return
      }

      const response = find({ questionId })(videoResponses)
      if (!response || !response.has) {
        allAnswered = false
      }
    })

    return allAnswered
  }

  const areRequiredAnswered = () => {
    const { videoResponses } = candidate
    const { questions } = interview

    if (!questions) {
      return true
    }

    return questions.every(({ _id: questionId, answerable, required }) => {
      if (!answerable || !required) {
        return true
      }
      const response = find({ questionId })(videoResponses)
      return response && response.has
    })
  }

  const continueLater = () => {
    if (model === 'previewCandidate') {
      if (onContinueLater) {
        onContinueLater()
      }
    } else if (candidate.completeUrl) {
      window.location.href = candidate.completeUrl
    } else {
      CandidateService.continueLater(candidate.uuid).then(() =>
        navigate('/saved')
      )
    }
  }

  const onSubmitInterview = () => {
    if (model === 'previewCandidate') {
      setAskConfirmation(true)
    } else {
      if (!areRequiredAnswered()) {
        setShowRequiredWarning(true)
        const questionsEl = document.querySelector('.interview-questions')
        if (questionsEl) {
          questionsEl.scrollIntoView({ behavior: 'smooth' })
        }
        return
      }
      const companyAndInterviewInfo =
        getCompanyAndInterviewInfoForAmplitude(interview)
      const candidateInfo = getCandidateInfoForAmplitude(candidate)
      const missingAnswers = !areAllAnswered() && !missingVideosConfirmed

      setAskConfirmation(true)
      analytics.onModalLoadEvent({
        locationId: 'interviewPage',
        id: missingAnswers ? 'confirmMissingVideos' : 'confirmSubmit',
        properties: {
          ...candidateInfo,
          ...companyAndInterviewInfo,
        },
      })
    }
  }

  const companyAndInterviewInfo =
    getCompanyAndInterviewInfoForAmplitude(interview)
  const candidateInfo = getCandidateInfoForAmplitude(candidate)
  const deadline = earlierDate(candidate.answerDeadline, interview.endTime)
  const missingAnswers = !areAllAnswered() && !missingVideosConfirmed
  const confirmHeader = missingAnswers
    ? i18n.interview.confirmSubmitHeader
    : i18n.interview.submitInterviewHeader

  const confirmContent = missingAnswers ? (
    <>
      <Body>{i18n.interview.confirmSubmitContent}</Body>
      <Body>{i18n.interview.confirmSubmitContentWarning}</Body>
    </>
  ) : (
    <Body>{i18n.interview.submitInterviewContent}</Body>
  )

  // TODO: action bar date format different from header date format in designs. I use the same  format as header for now

  return (
    <div className="interview">
      <InterviewFormWrapper
        onSubmit={onSubmitInterview}
        onConfirm={onConfirm}
        model={model}
        showRequiredWarning={showRequiredWarning}
        updateAdditionalFieldsValidityState={(shouldSubmitAdditionalFields) => {
          setShouldSubmitAdditionalFields(shouldSubmitAdditionalFields)
        }}
      >
        <ActionBarWrapper
          hideBeforeScroll
          leftSideComponent={
            deadline && (
              <Body fontWeight={600}>
                {i18n.layout.endTime(formatDateTimeTimezone(deadline))}
              </Body>
            )
          }
          actions={
            <>
              <Button
                variant="secondary"
                onClick={continueLater}
                disabled={
                  videoUploads.length > 0 ||
                  recording ||
                  !candidate.agreeTOS ||
                  isLoading
                }
                text={i18n.interview.continueLater}
                icon="access_time"
                event={
                  model === 'candidate' && {
                    locationId: 'interviewPage',
                    id: 'candidateContinueLaterButton',
                    properties: {
                      ...candidateInfo,
                      ...companyAndInterviewInfo,
                    },
                  }
                }
              />
              <Button
                type="submit"
                onClick={() => document.interviewForm.submit()}
                loading={isLoading}
                disabled={
                  videoUploads.length > 0 || recording || !candidate.agreeTOS
                }
                text={i18n.interview.submit}
                icon="send"
                flippedIcon={interview.language === 'ar'}
              />
            </>
          }
        />
      </InterviewFormWrapper>
      <Confirmation
        id="submit-confirm-modal"
        isOpen={askConfirmation}
        title={confirmHeader}
        body={confirmContent}
        isLoading={isLoading}
        flippedView={interview.language === 'ar'}
        size="medium"
        primaryButtonIcon="send"
        primaryButtonText={i18n.interview.confirmSubmitButton}
        onCancel={() => setAskConfirmation(false)}
        onConfirm={onConfirm}
        useConfirm
      />
    </div>
  )
}

Interview.propTypes = propTypes
Interview.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(Interview)
